import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, MessageSquare, AlertCircle, ArrowLeft } from 'lucide-react';
import { commentService } from '../services/commentService';
import { formatDate } from '../utils/dateUtils';
import '../styles/AllCommentsPage.css';

const AllCommentsPage = () => {
  const [params, setParams] = useState({
    retailAssoc: '',
    period: '',
  });
  
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paramsError, setParamsError] = useState(false);

  // Parse URL parameters
  const parseUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      retailAssoc: urlParams.get('Retail_Assoc_Key') || '',
      period: urlParams.get('Period_Key') || '',
    };
  };

  // Validate that required params are present (only Period and RA - no COA needed)
  const validateParams = (params) => {
    return !!(params.retailAssoc && params.period);
  };

  // Fetch all comments for the given Period and RA
  const fetchAllComments = useCallback(async () => {
    if (!validateParams(params)) return;
    
    setLoading(true);
    setError('');
    try {
      const data = await commentService.getAllCommentsByPeriodAndRA(params);
      const sorted = [...data].sort((a, b) => 
        new Date(b.created) - new Date(a.created)
      );
      setComments(sorted);
    } catch (err) {
      console.error('Error fetching all comments:', err);
      setError('Failed to load comments. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Initialize params from URL on mount
  useEffect(() => {
    const urlParams = parseUrlParams();
    console.log('All Comments Page - Parsed URL Parameters:', urlParams);
    
    setParams({
      retailAssoc: urlParams.retailAssoc,
      period: urlParams.period,
    });
    setParamsError(!validateParams(urlParams));
  }, []);

  // Fetch comments when params change
  useEffect(() => {
    if (validateParams(params)) {
      fetchAllComments();
    }
  }, [params, fetchAllComments]);

  return (
    <div className="all-comments-page">
      <div className="all-comments-container">
        {/* Header */}
        <div className="all-comments-header-card">
          <div className="all-comments-header-title">
            <MessageSquare className="header-icon" size={28} />
            <h1>All Comments</h1>
          </div>
          
          <div className="all-comments-header-params">
            <div className="param-item">
              <span className="param-label">Time:</span>
              <span className="param-value">{params.period}</span>
            </div>
            <div className="param-item">
              <span className="param-label">RA:</span>
              <span className="param-value">{params.retailAssoc}</span>
            </div>
          </div>
          
          <p className="all-comments-subtitle">
            Showing all comments for this Time Period and Retail Association.
          </p>
          
          {paramsError && (
            <div className="error-banner">
              <AlertCircle size={20} />
              <span>Passing in Time Period and Retail Association is mandatory.</span>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-banner standalone">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Comments Table */}
        <div className="all-comments-table-card">
          <div className="table-header">
            <h2>Comments ({comments.length})</h2>
            <button
              onClick={fetchAllComments}
              disabled={loading || paramsError}
              className="refresh-button"
            >
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
              Refresh
            </button>
          </div>
          
          <div className="table-wrapper">
            <table className="comments-table">
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>UserName</th>
                  <th>COA</th>
                  <th>LineItem</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      Loading comments...
                    </td>
                  </tr>
                ) : comments.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      No comments found for this Time Period and Retail Association.
                    </td>
                  </tr>
                ) : (
                  comments.map((comment, index) => (
                    <tr key={index}>
                      <td className="date-cell">{formatDate(comment.created)}</td>
                      <td className="username-cell">{comment.user}</td>
                      <td className='coa-cell'>{comment.coa}</td>
                      <td className="lineitem-cell">{comment.lineItem}</td>
                      <td className="comment-cell">{comment.comment}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Back Link */}
        <div className="back-link-container">
          <button 
            onClick={() => window.close()}
            className="back-button"
          >
            <ArrowLeft size={16} />
            Close this tab
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllCommentsPage;